/**
 * SummaryTab — overall score cards, tie-break rule, bias self-check,
 * and promotion eligibility section
 */
import { useState } from 'react';
import {
  C, BIAS_CHECKS, RATING_SCALE, ALL_LEVELS,
  getRatingColor, getRatingLabel,
} from '../../data/managerReviewData';
import type { Employee, BehaviorData, ActiveSidebar } from '../../types/managerReview';

interface SummaryTabProps {
  employee: Employee;
  level: string;
  behaviorsData: Record<string, BehaviorData>;
  behaviorsRating: number | null;
  deliveryRating: number | null;
  promotion: 'yes' | 'no' | null;
  setPromotion: (v: 'yes' | 'no') => void;
  promotionNote: string;
  setPromotionNote: (n: string) => void;
  overallRating: number | null;
  setOverallRating: (r: number | null) => void;
  overallRatingNote: string;
  setOverallRatingNote: (n: string) => void;
  promoTrack: 'E' | 'M';
  setPromoTrack: (t: 'E' | 'M') => void;
  setActiveSidebar: (s: ActiveSidebar) => void;
  biasChecked: Record<number, boolean>;
  setBiasChecked: (fn: (prev: Record<number, boolean>) => Record<number, boolean>) => void;
  biasNote: string;
  setBiasNote: (n: string) => void;
}

export default function SummaryTab({
  employee,
  level,
  behaviorsRating,
  deliveryRating,
  promotion,
  setPromotion,
  promotionNote,
  setPromotionNote,
  overallRating,
  setOverallRating,
  overallRatingNote,
  setOverallRatingNote,
  promoTrack,
  setPromoTrack,
  setActiveSidebar,
  biasChecked,
  setBiasChecked,
  biasNote,
  setBiasNote,
}: SummaryTabProps) {
  const bAvg = behaviorsRating ?? null;
  const overall = bAvg && deliveryRating ? (bAvg + deliveryRating) / 2 : null;
  const isTie = overall !== null && (overall * 10) % 5 === 0 && !Number.isInteger(overall);
  const suggestedRating = isTie ? deliveryRating : (overall ? Math.round(overall) : null);
  const deviation = overallRating && suggestedRating && overallRating !== suggestedRating;
  const isIC4 = level === 'IC4';
  const [promoConfirmed, setPromoConfirmed] = useState(false);
  const levelIdx = ALL_LEVELS.indexOf(level as typeof ALL_LEVELS[number]);
  const nextLevel = isIC4 ? (promoTrack === 'E' ? 'E5' : 'M5') : (levelIdx >= 0 && levelIdx < ALL_LEVELS.length - 1 ? ALL_LEVELS[levelIdx + 1] : null);
  const allChecked = Object.values(biasChecked).filter(Boolean).length === BIAS_CHECKS.length;

  return (
    <div>
      {/* Overall Rating card */}
      <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}`, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Rating Overview</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
          Your overall rating is the average of your Behavior and Delivery ratings, rounded to the nearest whole number. If the result lands exactly on a .5, Delivery takes precedence.
        </div>

        {/* Score cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div style={{ background: '#f5f5f5', borderRadius: 10, padding: '20px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Behavior Avg</div>
            <div style={{ fontSize: 44, fontWeight: 800, color: '#1a1a1a', lineHeight: 1 }}>
              {bAvg ? (Number.isInteger(bAvg) ? bAvg : bAvg.toFixed(1)) : '—'}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{bAvg ? getRatingLabel(Math.round(bAvg)) : 'Not yet rated'}</div>
          </div>
          <div style={{ background: '#f5f5f5', borderRadius: 10, padding: '20px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Delivery</div>
            <div style={{ fontSize: 44, fontWeight: 800, color: '#1a1a1a', lineHeight: 1 }}>{deliveryRating || '—'}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{deliveryRating ? getRatingLabel(deliveryRating) : 'Not yet rated'}</div>
          </div>
          {(() => {
            const col = suggestedRating ? getRatingColor(suggestedRating) : null;
            return (
              <div style={{ background: col ? col + '18' : '#f5f5f5', border: `2px solid ${col ?? '#e0e0e0'}`, borderRadius: 10, padding: '20px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: col ?? C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Overall</div>
                <div style={{ fontSize: 44, fontWeight: 800, color: col ?? '#ccc', lineHeight: 1 }}>{suggestedRating || '—'}</div>
                <div style={{ fontSize: 12, color: col ?? C.muted, marginTop: 8 }}>
                  {isTie ? 'Tie → Delivery' : suggestedRating ? getRatingLabel(suggestedRating) : 'Awaiting ratings'}
                </div>
              </div>
            );
          })()}
        </div>

        {!bAvg && <div style={{ padding: 12, background: 'rgba(232,41,74,0.05)', border: '1px solid rgba(232,41,74,0.3)', borderRadius: 8, fontSize: 13, color: C.text, marginBottom: 16 }}>⚠️ Enter your Overall Behaviors Rating in the Behaviors tab to see the final score.</div>}
        {bAvg && !deliveryRating && <div style={{ padding: 12, background: 'rgba(232,41,74,0.05)', border: '1px solid rgba(232,41,74,0.3)', borderRadius: 8, fontSize: 13, color: C.text, marginBottom: 16 }}>⚠️ Enter your Overall Delivery Rating in the Delivery tab to see the final score.</div>}

        {overall && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                <tr style={{ borderBottom: `1px solid ${C.border}`, background: '#f9f9f9' }}>
                  <td style={{ padding: '11px 0', fontSize: 12, color: C.muted }}>
                    Suggested
                    {isTie && (
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 3, lineHeight: 1.5 }}>
                        Score is exactly {overall.toFixed(1)} — Delivery takes precedence as tie-breaker.
                      </div>
                    )}
                    {!isTie && overall && (
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>
                        (Delivery {deliveryRating} + Behaviors {bAvg}) ÷ 2 = {overall.toFixed(2)}, rounded to {suggestedRating}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', padding: '11px 0' }}>
                    {suggestedRating && (() => {
                      const col = getRatingColor(suggestedRating);
                      return (
                        <span style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 7, background: col + '18', border: `1.5px solid ${col}`, color: col, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {suggestedRating} – {getRatingLabel(suggestedRating)}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 0', fontSize: 12, fontWeight: 700, color: C.text, verticalAlign: 'middle' }}>Your Rating</td>
                  <td style={{ textAlign: 'right', padding: '12px 0', verticalAlign: 'middle' }}>
                    <select
                      value={overallRating || ''}
                      onChange={e => setOverallRating(e.target.value ? Number(e.target.value) : null)}
                      style={{
                        padding: '7px 10px', borderRadius: 7,
                        border: `1.5px solid ${overallRating ? getRatingColor(overallRating) : C.border}`,
                        fontSize: 13, fontFamily: 'Inter', fontWeight: 600,
                        color: overallRating ? getRatingColor(overallRating) : C.muted,
                        background: overallRating ? getRatingColor(overallRating) + '18' : '#fff',
                        outline: 'none', cursor: 'pointer',
                      }}
                    >
                      <option value=''>Select…</option>
                      {RATING_SCALE.map(r => <option key={r.v} value={r.v}>{r.v} – {r.label}</option>)}
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>

            {deviation && (
              <div style={{ marginTop: 14, padding: '13px 15px', background: '#f8f8f8', borderRadius: 10, borderLeft: '3px solid #d1d5db' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#444', marginBottom: 6 }}>Your rating differs from the suggestion — briefly note why.</div>
                <textarea
                  value={overallRatingNote}
                  onChange={e => setOverallRatingNote(e.target.value)}
                  placeholder="e.g. Delivery significantly outweighed behaviors in impact this half..."
                  rows={3}
                  style={{ width: '100%', borderRadius: 8, border: '1.5px solid #d1d5db', padding: '9px 11px', fontSize: 12, fontFamily: 'Inter', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55, outline: 'none', background: '#fff', color: '#333' }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Promotion card — hidden for C-Level (M9) */}
      {level !== 'M9' && <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}`, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 18 }}>Promotion</div>
        {(() => {
          const effectiveRating = overallRating ?? suggestedRating;
          const rd = effectiveRating ? RATING_SCALE.find(r => r.v === effectiveRating) : null;
          const ratingEligibleForPromo = rd ? rd.promotion : false;
          const hasNoReviewHistory = !employee.isFirstCycle && !employee.lastOverallRating && !employee.lastReviewOutcome;
          // When no history, we can't verify the consecutive-promotion rule — treat as unknown (null = "—")
          const notConsecutive: boolean | null = hasNoReviewHistory ? null : !employee.previouslyPromoted;
          const promoEligible = ratingEligibleForPromo && notConsecutive === true;
          const bothFail = promotion === 'yes' && !ratingEligibleForPromo && notConsecutive === false;
          const ratingFail = promotion === 'yes' && !ratingEligibleForPromo && notConsecutive !== false;
          const consecutiveFail = promotion === 'yes' && ratingEligibleForPromo && notConsecutive === false;

          const criteriaIcon = (met: boolean | null) => (
            <div style={{
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 800,
              background: met === null ? '#f0f0f0' : met ? 'rgba(38,189,89,0.12)' : 'rgba(232,41,74,0.10)',
              color: met === null ? '#aaa' : met ? '#26bd59' : C.red,
            }}>
              {met === null ? '–' : met ? '✓' : '✕'}
            </div>
          );

          return (
            <>
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Promotion Decision</div>
                  <a href="https://finn.atlassian.net/wiki/spaces/HR/pages/2503443475/The+Calibration+Rules" target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 11, color: C.blue, fontWeight: 600, textDecoration: 'none' }}>
                    Full Calibration Rules ↗
                  </a>
                </div>

                <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 10 }}>Eligibility</div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
                  {criteriaIcon(rd ? ratingEligibleForPromo : null)}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a', marginBottom: 2 }}>Overall Performance Rating &gt; 3</div>
                    <div style={{ fontSize: 11, color: C.muted }}>Promotion requires exceeding current level expectations.</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', marginBottom: 12 }}>
                  {criteriaIcon(notConsecutive)}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a', marginBottom: 2 }}>No back-to-back promotion</div>
                    <div style={{ fontSize: 11, color: C.muted }}>People need time to settle and prove sustained performance.</div>
                  </div>
                </div>

                {!hasNoReviewHistory && (() => {
                  const alertStyle = { borderRadius: '0 6px 6px 0', padding: '10px 12px', fontSize: 12 };
                  if (!rd) return (
                    <div style={{ ...alertStyle, background: '#f5f8ff', borderLeft: '3px solid #0087eb', color: '#0060b0' }}>
                      <strong style={{ display: 'block', marginBottom: 4 }}>Eligibility pending</strong>
                      Set the overall rating above to check promotion eligibility.
                    </div>
                  );
                  if (promoEligible) return (
                    <div style={{ ...alertStyle, background: 'rgba(38,189,89,0.06)', borderLeft: '3px solid #26bd59', color: '#1a7a38' }}>
                      <strong style={{ display: 'block', marginBottom: 4 }}>Eligible for promotion this cycle</strong>
                      Both criteria are met — you can nominate this employee for promotion.
                    </div>
                  );
                  if (!ratingEligibleForPromo && notConsecutive === false) return (
                    <div style={{ ...alertStyle, background: 'rgba(232,41,74,0.05)', borderLeft: `3px solid ${C.red}`, color: '#b0001e' }}>
                      <strong style={{ display: 'block', marginBottom: 4 }}>Not eligible this cycle</strong>
                      A rating of 4 or 5 is required for promotion, and {employee.name.split(' ')[0]} was promoted last cycle.
                    </div>
                  );
                  if (!ratingEligibleForPromo) return (
                    <div style={{ ...alertStyle, background: 'rgba(232,41,74,0.05)', borderLeft: `3px solid ${C.red}`, color: '#b0001e' }}>
                      <strong style={{ display: 'block', marginBottom: 4 }}>Not eligible this cycle</strong>
                      A rating of 4 or 5 is required for promotion.
                    </div>
                  );
                  return (
                    <div style={{ ...alertStyle, background: 'rgba(232,41,74,0.05)', borderLeft: `3px solid ${C.red}`, color: '#b0001e' }}>
                      <strong style={{ display: 'block', marginBottom: 4 }}>Not eligible this cycle</strong>
                      {employee.name.split(' ')[0]} was promoted last cycle.
                    </div>
                  );
                })()}
              </div>

              {hasNoReviewHistory && (
                <div style={{
                  marginTop: 14, padding: '11px 14px', borderRadius: 8,
                  background: 'rgba(0,135,235,0.06)', border: '1px solid rgba(0,135,235,0.2)',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                  <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
                  <div style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                    No previous cycle data found for this employee (e.g. parental leave, role exempt from standard review).
                    Promotion eligibility cannot be auto-verified — please check with your People &amp; Organization Partner if unsure.
                  </div>
                </div>
              )}

              <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 18, marginBottom: 18 }} />
              <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>Your decision</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginBottom: 12 }}>
                I am putting this employee forward for promotion this cycle.
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {(['yes', 'no'] as const).map(val => (
                  <button key={val} onClick={() => setPromotion(val)} style={{
                    padding: '9px 24px', border: `2px solid ${promotion === val ? C.blue : C.border}`,
                    borderRadius: 8, background: promotion === val ? C.lightBlue : '#fff',
                    color: promotion === val ? C.blue : C.muted,
                    fontWeight: promotion === val ? 700 : 500,
                    fontSize: 13, cursor: 'pointer', fontFamily: 'Inter', transition: 'all 0.15s',
                  }}>
                    {val === 'yes' ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>

              {bothFail && (
                <div style={{ borderRadius: '0 6px 6px 0', padding: '10px 12px', background: 'rgba(232,41,74,0.05)', borderLeft: `3px solid ${C.red}`, marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#b0001e', marginBottom: 4 }}>Consider this carefully</div>
                  <div style={{ fontSize: 12, color: '#b0001e', lineHeight: 1.6 }}>
                    A rating of 4 or 5 is required for promotion, and {employee.name.split(' ')[0]} was promoted last cycle.
                  </div>
                </div>
              )}
              {ratingFail && (
                <div style={{ borderRadius: '0 6px 6px 0', padding: '10px 12px', background: 'rgba(232,41,74,0.05)', borderLeft: `3px solid ${C.red}`, marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#b0001e', marginBottom: 4 }}>Consider this carefully</div>
                  <div style={{ fontSize: 12, color: '#b0001e', lineHeight: 1.6 }}>
                    A rating of 4 or 5 is required for promotion.
                  </div>
                </div>
              )}
              {consecutiveFail && (
                <div style={{ borderRadius: '0 6px 6px 0', padding: '10px 12px', background: 'rgba(232,41,74,0.05)', borderLeft: `3px solid ${C.red}`, marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#b0001e', marginBottom: 4 }}>Consider this carefully</div>
                  <div style={{ fontSize: 12, color: '#b0001e', lineHeight: 1.6 }}>
                    {employee.name.split(' ')[0]} was promoted last cycle.
                  </div>
                </div>
              )}

              {promotion === 'yes' && (!ratingEligibleForPromo || !notConsecutive) && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 2 }}>
                    Your reasoning
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                    Helps generate a stronger AI review and gives you a clear argument ready if this decision is challenged in calibration.
                  </div>
                  <textarea
                    value={promotionNote}
                    onChange={e => setPromotionNote(e.target.value)}
                    placeholder="Make the case specifically. What evidence shows this person is already operating at the level above?"
                    rows={3}
                    style={{ width: '100%', borderRadius: 8, border: `1.5px solid ${C.red}`, padding: '9px 11px', fontSize: 12, fontFamily: 'Inter', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55, outline: 'none', background: 'rgba(232,41,74,0.04)', color: '#333' }}
                  />
                </div>
              )}

              {promotion === 'yes' && nextLevel && (
                <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 18, paddingTop: 16 }}>
                  {isIC4 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                      <span style={{ fontSize: 12, color: C.muted }}>Promotion track:</span>
                      {(['E', 'M'] as const).map(t => (
                        <button key={t} onClick={() => setPromoTrack(t)} style={{
                          padding: '3px 10px', borderRadius: 12,
                          border: `1.5px solid ${promoTrack === t ? C.blue : C.border}`,
                          background: promoTrack === t ? C.blue : '#fff',
                          color: promoTrack === t ? '#fff' : C.muted,
                          fontSize: 12, fontWeight: promoTrack === t ? 700 : 400,
                          cursor: 'pointer', fontFamily: 'Inter',
                        }}>
                          {t === 'E' ? 'Expert (E5)' : 'Leadership (M5)'}
                        </button>
                      ))}
                    </div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={promoConfirmed}
                      onChange={() => setPromoConfirmed(p => !p)}
                      style={{ marginTop: 3, width: 15, height: 15, accentColor: C.blue, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 13, lineHeight: 1.6, color: C.text, fontWeight: promoConfirmed ? 600 : 400 }}>
                      I am confident that {employee.name.split(' ')[0]} can grow into the expectations of {nextLevel} within the first 6 months after promotion.
                    </span>
                  </label>
                  <button
                    onClick={() => setActiveSidebar('promoframework')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', borderRadius: 7, border: 'none',
                      background: '#1a1a1a', color: '#fff', fontSize: 12,
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter',
                    }}
                  >
                    📋 View Framework — {nextLevel} →
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>}

      {/* Bias Self-Check */}
      <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}`, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🪞 Bias Self-Check</div>
        <p style={{ color: C.muted, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>Confirm each applies before finalizing your review:</p>
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
          {BIAS_CHECKS.map((check, i) => (
            <div
              key={i}
              onClick={() => setBiasChecked(p => ({ ...p, [i]: !p[i] }))}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 0', cursor: 'pointer',
                borderBottom: i < BIAS_CHECKS.length - 1 ? '1px solid #f5f5f5' : 'none',
              }}
            >
              <div style={{
                width: 17, height: 17, border: `1.5px solid ${biasChecked[i] ? '#26bd59' : '#d0d0d0'}`,
                borderRadius: 4, flexShrink: 0, marginTop: 1,
                background: biasChecked[i] ? '#26bd59' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800, color: '#fff',
                transition: 'all 0.15s',
              }}>
                {biasChecked[i] ? '✓' : null}
              </div>
              <span style={{ fontSize: 12, lineHeight: 1.5, color: biasChecked[i] ? C.text : '#444', flex: 1 }}>{check}</span>
            </div>
          ))}
        </div>
        {allChecked && (
          <div style={{ padding: 10, background: 'rgba(38,189,89,0.08)', border: '1px solid rgba(38,189,89,0.3)', borderRadius: 6, fontSize: 13, color: '#1a7a38', fontWeight: 500, marginBottom: 18 }}>
            ✅ All checks complete. Good work!
          </div>
        )}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>
            Anything I should keep in mind? <span style={{ fontWeight: 400, color: C.muted, fontSize: 12 }}>optional</span>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, lineHeight: 1.5 }}>
            Is there anything about your perspective, context, or potential blind spots that the AI should factor in?
          </div>
          <textarea
            value={biasNote}
            onChange={e => setBiasNote(e.target.value)}
            placeholder="e.g. I may have overweighted recent wins from Q4 — please balance this against the broader year."
            rows={3}
            style={{ width: '100%', borderRadius: 8, border: `1.5px solid ${C.border}`, padding: '9px 11px', fontSize: 12, fontFamily: 'Inter', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55, outline: 'none', background: '#fafafa', color: '#333' }}
          />
        </div>
      </div>
    </div>
  );
}
